import type { Instance } from 'flatpickr/dist/types/instance';

/**
 * Applies variant props (theme attributes) to a DOM element
 */
export function applyVariantProps(element: HTMLElement, variantProps: Record<string, string>): void {
  for (const [key, value] of Object.entries(variantProps)) {
    element.setAttribute(key, value);
  }
}

/**
 * Sets up a MutationObserver to sync the 'active' attribute based on the 'selected' class.
 * Returns a cleanup function to disconnect the observer.
 */
export function setupActiveSyncObserver(dayElem: HTMLElement): () => void {
  const syncActiveState = () => {
    if (dayElem.classList.contains('selected')) {
      dayElem.setAttribute('active', '');
    } else {
      dayElem.removeAttribute('active');
    }
  };

  // Run once initially
  syncActiveState();

  // Observe class changes (selected/unselected)
  const observer = new MutationObserver(syncActiveState);
  observer.observe(dayElem, { attributes: true, attributeFilter: ['class'] });

  // Return cleanup function
  return () => observer.disconnect();
}

/**
 * Applies external CSS classes to the calendar container
 */
export function applyExternalClasses(container: HTMLElement, className?: string): void {
  if (!className) return;

  className.split(' ').forEach((externalClass) => {
    if (externalClass.trim()) {
      container.classList.add(externalClass);
    }
  });
}

/**
 * Sets up dialog-specific behavior: ensures single-click opens the calendar
 * Returns a cleanup function to remove event listeners.
 */
export function setupDialogBehavior(calendarInstance: Instance): () => void {
  const activeInput = (calendarInstance as any).altInput || calendarInstance.input;

  if (!activeInput) {
    return () => {}; // No-op cleanup
  }

  const onFocusOpen = () => {
    if (!calendarInstance.isOpen) {
      calendarInstance.open();
    }
  };

  const onMouseDownOpen = () => {
    if (!calendarInstance.isOpen) {
      calendarInstance.open();
    }
  };

  activeInput.addEventListener('focus', onFocusOpen);
  activeInput.addEventListener('mousedown', onMouseDownOpen);

  // Return cleanup function
  return () => {
    activeInput.removeEventListener('focus', onFocusOpen);
    activeInput.removeEventListener('mousedown', onMouseDownOpen);
  };
}
